import {finalize} from 'rxjs/operators';
import { Component, OnInit, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { DLCompany } from './company';
import { NotifierService, DialogService, TOption } from '../../ui';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DLLedger } from './ledger';
import { DLLedgerService } from './ledger.service';
import { find, first, sortBy } from 'lodash';
import { DLSettingsService } from '../settings/settings.service';
import { DLLedgerTemplate } from '../settings/model';

type ViewMode = 'view' | 'new' | 'edit';

type NameSource = 'SELECT' | 'OTHER';

@Component({
  templateUrl: './ledger-edit.component.html',
})
export class DLLedgerEditComponent implements OnInit, AfterViewInit {

  company: DLCompany
  ledger: DLLedger

  mode: ViewMode
  title: string

  ledgerTemplates: DLLedgerTemplate[]
  nameSourceOptions: TOption[] = [
    {
      text: 'Sélectioner un nom de la liste',
      value: 'SELECT',
    },
    {
      text: 'Autre',
      value: 'OTHER',
    }
  ]
  nameSource: NameSource = 'OTHER';

  @ViewChild('nameInput')
  nameInput: ElementRef

  formGroup: FormGroup
  name: FormControl
  templatesSelect: FormControl

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private ledgerService: DLLedgerService,
    private settingsService: DLSettingsService,
    private notifier: NotifierService,
    private dialogService: DialogService
  ) { }

  initForm() {

    if (this.mode === 'new') {
      if (this.ledgerTemplates.length > 0) {
        this.ledger.name = this.ledgerTemplates[0].name;
        this.ledger.signed = this.ledgerTemplates[0].signed;
        this.nameSource = 'SELECT';
      }
    }
    else if (this.mode === 'edit') {
      if (this.ledgerTemplates.length > 0) {
        const template = find(this.ledgerTemplates, t => t.name === this.ledger.name);

        if (template) {
          this.nameSource = 'SELECT';
        }
      }
    }

    this.formGroup = new FormGroup({});
    
    // templatesSelect
    let selectedTemplate = undefined;

    if (this.nameSource === 'SELECT') {
      selectedTemplate = find(this.ledgerTemplates, t => this.ledger.name === t.name);
    }

    this.templatesSelect = new FormControl(selectedTemplate);
    
    this.templatesSelect.valueChanges.subscribe((value) => {
      this.ledger.name = value.name;
      this.ledger.signed = value.signed;
    });
    
    // name
    this.name = new FormControl(this.ledger.name, [
      Validators.required
    ]);
    
    this.name.valueChanges.subscribe((value) => {
      this.ledger.name = value;
    });

    this.formGroup.addControl('templatesSelect', this.templatesSelect);
    this.formGroup.addControl('name', this.name);
  }
  
  ngAfterViewInit() {
    // Focus name input
    if (this.nameInput) {
      this.nameInput.nativeElement.focus();
    }
  }

  ngOnInit() {

    this.mode = this.route.routeConfig.data.mode;
    this.title = this.route.routeConfig.data.title;

    switch (this.mode) {
      case 'new': 
        {
          // Get the company
          this.notifier.startProgress();

          this.settingsService.settings().subscribe((settings) => {
            this.ledgerTemplates = settings.ledgerTemplates;

            this.route.data.subscribe((data) => {
              this.company = data.company;
              
              this.ledger = new DLLedger({
                signed: true,
              });
              
              this.initForm();
              
              this.notifier.endProgress();
            });
          });
        }
        break;

      case 'edit':
        {
          // Get the company + ledger
          this.notifier.startProgress();

          this.settingsService.settings().subscribe((settings) => {
            this.ledgerTemplates = settings.ledgerTemplates;

            this.route.data.subscribe((data) => {
              this.company = data.company;
              this.ledger = first(this.company.ledgers);

              this.initForm();

              this.notifier.endProgress();
            });
          });
        }
        break;

      case 'view':
        {
          // Get the company
          this.notifier.startProgress();

          this.route.data.subscribe((data) => {
            this.company = data.company;
            this.ledger = first(this.company.ledgers);

            this.notifier.endProgress();
          });
        }
        break;
    }

    // Sort documents
    if (this.ledger) {
      this.ledger.documents = sortBy(this.ledger.documents, d => d.filename);
    }
  }

  save() {
    if (!this.ledger || !this.ledger.valid) {
      return;
    }

    let notifier = this.notifier;

    notifier.updateProgress(20);

    function onComplete() {
      notifier.endProgress();
    }

    switch (this.mode) {
      case 'new':
        {
          this.ledgerService
            .create(this.company.id, this.ledger).pipe(
            finalize(onComplete))
            .subscribe((ledger) => {
              this.notifier.success('Registre créé avec succès');

              // Got to view screen
              this.router.navigate(['documents-ledger', 'companies', this.company.id, ledger.id]);
            });
        }
        break;

      case 'edit':
        {
          this.ledgerService
            .update(this.company.id, this.ledger.id, this.ledger).pipe(
            finalize(onComplete))
            .subscribe((ledger) => {
              this.notifier.success('Registre enregistré avec succès');

              // Got to view screen
              this.router.navigate(['documents-ledger', 'companies', this.company.id, ledger.id]);
            });
        }
        break;
    }
  }

  cancel() {
    if (this.mode === 'edit') {
      this.router.navigate(['documents-ledger', 'companies', this.company.id, this.ledger.id]);
    }
    else {
      this.router.navigate(['documents-ledger', 'companies', this.company.id]);
    }
  }

  delete() {
    this.dialogService
      .confirmDelete('Voulez-vous supprimer ce registre?')
      .subscribe((result) => {
        if (!result) {
          return;
        }

        this.notifier.startProgress();

        this.ledgerService
          .delete(this.company.id, this.ledger.id)
          .subscribe((id) => {
            this.notifier.success('Registre supprimé avec succès');

            this.router.navigate(['documents-ledger', 'companies', this.company.id]);

          }, (err) => {
            this.notifier.endProgress();
          });
      });
    
    return false;
  }
}
